// OWNER: TELEPHONY agent (texting, owner request 2026-10-03). The Texts inbox: every conversation on our numbers that
// is mine (or, with "All", every one I can see), unread first. Left: conversations; right: the conversation, who it is
// for, and the composer. /texts/<id> opens one. Opening a conversation marks it read for the person it is for.
// An unmatched conversation offers its candidate businesses (called from that number in the 15 minutes before the text,
// or that already used that number with us) as one-click "Link to ..." buttons; "Replied STOP" marks a number we never
// text again. "New text" (/texts/new, owner request 2026-10-04) texts any number someone gave you.
import '../dialer/dialer.css';
import '../dialer/sms.css';
import { useEffect, useRef, useState } from 'preact/hooks';
import type { SmsNewTextView, SmsThreadDetail, SmsThreadItem } from '../../../src/shared/api-sms';
import { SMS_US_PENDING_NOTE } from '../../../src/shared/api-sms';
import type { UserAdmin } from '../../../src/shared/api-core';
import { areaCodeRegion } from '../../../src/shared/area-codes';
import { api, errorMessage } from '../api';
import { navigate } from '../app';
import { Empty, ErrorBanner, toast } from '../components/ui';
import { Segmented, Spinner, useDebounced } from '../components/ui-extra';
import { endpoints } from '../dialer/endpoints';
import { fmtPhone, toE164Nanp } from '../dialer/labels';
import { callableMemberships } from '../dialer/leads';
import { SmsBubbles, SmsComposer } from '../dialer/SmsConversation';
import { LinkBusiness } from '../dialer/LinkBusiness';
import { isConsentNeeded, sendText, sendToast, smsApi, smsAssistFor, smsTime, threadTitle, usTextPending } from '../dialer/sms';
import { openMembership } from '../dialer/store';
import { useMe } from '../me';
import { refreshSmsUnread, useSmsUnread } from '../texts';

const LIST_POLL_MS = 15_000;
const THREAD_POLL_MS = 10_000;

function ThreadRow(props: { t: SmsThreadItem; on: boolean; showFor: boolean }) {
  const t = props.t;
  return (
    <button class={`vd-texts-item ${props.on ? 'on' : ''} ${t.unread ? 'vd-texts-unread' : ''}`} aria-current={props.on ? 'true' : undefined}
      onClick={() => navigate(`/texts/${t.id}`)}>
      <span class="vd-texts-item-top">
        <span class="vd-texts-name">{threadTitle(t)}</span>
        {t.unread ? <span class="vd-texts-dot" title={`${t.unread} unread`}>{t.unread}</span> : null}
        <span class="vd-texts-when">{smsTime(t.lastMessageAt)}</span>
      </span>
      <span class="vd-texts-preview">{t.lastDirection === 'out' ? 'You: ' : ''}{t.lastPreview || '—'}</span>
      <span class="vd-texts-chips">
        {t.business ? <span class="muted" style="font-size:12px">{fmtPhone(t.theirE164)}</span> : null}
        {t.linkSource === 'recent_call' ? <span class="chip chip-info">Matched from your call</span> : null}
        {!t.business ? <span class="chip chip-warn">Unmatched</span> : null}
        {t.optedOut ? <span class="chip chip-bad">Replied STOP</span> : null}
        {props.showFor ? <span class="chip">{t.assignedUserName ?? 'Any admin'}</span> : null}
      </span>
    </button>
  );
}

function Conversation(props: { id: number; users: UserAdmin[] | null; onChanged: () => void }) {
  const [d, setD] = useState<SmsThreadDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [linking, setLinking] = useState(false);
  const [opening, setOpening] = useState(false);
  const [forceConsent, setForceConsent] = useState(false);   // the server asked for the consent tick
  const seen = useRef(0);   // messages on screen, to refresh the badge only when something new was marked read

  const load = async (first = false) => {
    try {
      const x = await smsApi.thread(props.id);
      setD(x);
      setError(null);
      if (first || x.messages.length !== seen.current) void refreshSmsUnread();
      seen.current = x.messages.length;
    } catch (e) {
      setError(errorMessage(e));
    }
  };
  useEffect(() => {
    setD(null); setError(null); setLinking(false); setForceConsent(false); seen.current = 0;
    void load(true);
    const t = setInterval(() => void load(), THREAD_POLL_MS);
    return () => clearInterval(t);
  }, [props.id]);

  if (error && !d) return <div class="vd-texts-convo"><ErrorBanner error={error} /></div>;
  if (!d) return <div class="vd-texts-convo"><Spinner label="Loading the conversation" /></div>;
  const t = d.thread;
  const usPending = usTextPending(d.usA2pApproved, t.theirE164, t.ourE164);

  const send = async (body: string, consent: boolean): Promise<boolean> => {
    try {
      const r = await sendText({ threadId: t.id, body, ...(consent ? { consent: true } : {}) });
      if (!r) return false;
      const note = sendToast(r, t.theirE164, usPending);
      // A plain "sent" needs no toast here: the text shows in the conversation.
      if (note.kind !== 'ok') toast(note.text, note.kind);
      setForceConsent(false);
      await load();
      props.onChanged();
      return true;
    } catch (e) {
      if (isConsentNeeded(e)) setForceConsent(true);
      toast(errorMessage(e), 'bad');
      return false;
    }
  };
  const link = async (businessId: number, name: string) => {
    try {
      setD(await smsApi.link(t.id, businessId));
      setLinking(false);
      toast(`Filed under ${name}.`, 'ok');
      props.onChanged();
    } catch (e) {
      toast(errorMessage(e), 'bad');
    }
  };
  const assign = async (raw: string) => {
    try {
      setD(await smsApi.assign(t.id, raw ? Number(raw) : null));
      props.onChanged();
      void refreshSmsUnread();
    } catch (e) {
      toast(errorMessage(e), 'bad');
    }
  };
  const openLead = async () => {
    if (!t.business) return;
    setOpening(true);
    try {
      const detail = await endpoints.business(t.business.id);
      const m = callableMemberships({ business: detail.business, memberships: detail.memberships }, null)[0];
      if (!m) { toast("This business has no open lead to call (closed, do-not-contact, or in no campaign of yours).", 'warn'); return; }
      if ((await openMembership(m.id)) !== 'failed') navigate('/');
    } catch (e) {
      toast(errorMessage(e), 'bad');
    } finally { setOpening(false); }
  };

  return (
    <div class="vd-texts-convo">
      <div class="vd-texts-convo-head">
        <div class="row" style="justify-content:space-between;flex-wrap:wrap">
          <h2 style="margin:0">{threadTitle(t)}</h2>
          <div class="vd-texts-chips">
            {t.linkSource === 'recent_call' ? (
              <span class="chip chip-info" title="They texted the number that called this business within 15 minutes of the call, and that number called no other business then, so it was filed under that business. Check it's them.">Matched from your call</span>
            ) : null}
            {!t.business ? <span class="chip chip-warn">Unmatched</span> : null}
            {t.optedOut ? <span class="chip chip-bad" title="They replied STOP: no number of ours texts them again unless they reply START.">Replied STOP</span> : null}
          </div>
        </div>
        <div class="vd-texts-facts">
          <span>Them: <b>{fmtPhone(t.theirE164)}</b></span>
          <span>Our number: <b>{fmtPhone(t.ourE164)}</b></span>
          <span>For: <b>{t.assignedUserName ?? 'any admin'}</b></span>
        </div>
        <div class="vd-texts-tools">
          {t.business ? <button class="btn btn-sm" disabled={opening} onClick={() => void openLead()}>{opening ? 'Opening…' : 'Open lead in the dialer'}</button> : null}
          {d.canLink && !linking ? (
            <button class={`btn btn-sm ${t.business ? '' : 'btn-primary'}`} onClick={() => setLinking(true)}>{t.business ? 'Move to another business' : 'Link to a business'}</button>
          ) : null}
          {d.canAssign && props.users ? (
            <label class="row" style="gap:6px">For
              <select value={t.assignedUserId === null ? '' : String(t.assignedUserId)} onChange={(e) => void assign(e.currentTarget.value)}>
                <option value="">Any admin</option>
                {props.users.filter((u) => u.active || u.id === t.assignedUserId).map((u) => <option value={String(u.id)}>{u.name}{u.role === 'admin' ? ' (admin)' : ''}</option>)}
              </select>
            </label>
          ) : null}
        </div>
        {!t.business && d.canLink && d.linkCandidates.length && !linking ? (
          <div class="vd-texts-tools" role="group" aria-label="Possible businesses">
            <span class="muted" style="font-size:13px">Possibly:</span>
            {d.linkCandidates.map((cand) => (
              <button class="btn btn-sm" title="File this conversation under this business" onClick={() => void link(cand.id, cand.name)}>Link to {cand.name}</button>
            ))}
          </div>
        ) : null}
        {linking ? <LinkBusiness onPick={link} onCancel={() => setLinking(false)} /> : null}
        {error ? <ErrorBanner error={error} /> : null}
      </div>
      <SmsBubbles messages={d.messages} />
      <SmsComposer blocked={d.sendBlocked} country={d.country} calendlySlug={d.calendlySlug} onSend={send} focusKey={t.id}
        consentNeeded={t.consentNeeded || forceConsent} notice={usPending ? SMS_US_PENDING_NOTE : null}
        assist={smsAssistFor({ threadId: t.id, businessId: null, to: null, canSummarize: d.messages.length > 0 })} />
    </div>
  );
}

/** New text (owner request, PJ, 2026-10-04): text any number someone gave you (say a client who asked for a text
 *  instead of an email), optionally linked to a lead. Before sending, the server says what will happen (GET
 *  /api/sms/new-text): a conversation with that number you can already see takes the text (never a second one), a lead's
 *  number is texted as that lead, and anything that stops it is shown up front. Sending opens the conversation. */
function NewText(props: { onSent: (threadId: number) => void; onCancel: () => void }) {
  const [typed, setTyped] = useState('');
  const [lead, setLead] = useState<{ id: number; name: string } | null>(null);
  const [linking, setLinking] = useState(false);
  const [answer, setAnswer] = useState<{ key: string; view: SmsNewTextView | null; error: string | null } | null>(null);
  const [forceConsent, setForceConsent] = useState<string | null>(null);   // the server asked for the tick on this key
  const to = toE164Nanp(typed);
  const area = to ? areaCodeRegion(to.slice(2, 5)) : null;
  const key = to ? `${to}|${lead?.id ?? ''}` : '';
  const dKey = useDebounced(key, 300);
  useEffect(() => {
    if (!dKey) return;
    let live = true;
    const [num, biz] = dKey.split('|');
    smsApi.newText(num, biz ? Number(biz) : null).then(
      (v) => { if (live) setAnswer({ key: dKey, view: v, error: null }); },
      (e) => { if (live) setAnswer({ key: dKey, view: null, error: errorMessage(e) }); },
    );
    return () => { live = false; };
  }, [dKey]);

  // Only the answer for the number and lead on screen now, never a stale one while typing.
  const current = answer && answer.key === key ? answer : null;
  const view = current?.view ?? null;
  const blocked = !typed.trim() ? 'Type the number they gave you.'
    : !to ? 'Type all 10 digits of a US or Canadian number.'
    : !current ? 'Checking the number…'
    : current.error ?? view?.blocked ?? null;
  const consentNeeded = !!view && (view.consentNeeded || forceConsent === key);
  const usPending = !!view && usTextPending(view.usA2pApproved, to, view.from?.e164);

  const send = async (body: string, consent: boolean): Promise<boolean> => {
    if (!to || !view) return false;
    try {
      const r = await sendText({ to, body, ...(lead ? { businessId: lead.id } : {}), ...(consent ? { consent: true } : {}) });
      if (!r) return false;
      const note = sendToast(r, to, usPending);
      if (note.kind !== 'ok') toast(note.text, note.kind);
      if (!r.newConversation) toast(`You already had a conversation with ${fmtPhone(to)}, so the text went into it.`, 'info');
      else if (note.kind === 'ok') toast(note.text, 'ok');
      props.onSent(r.threadId);
      return true;
    } catch (e) {
      if (isConsentNeeded(e)) setForceConsent(key);
      toast(errorMessage(e), 'bad');
      return false;
    }
  };

  return (
    <div class="vd-texts-convo">
      <div class="vd-texts-convo-head">
        <div class="row" style="justify-content:space-between">
          <h2 style="margin:0">New text</h2>
          <button class="btn btn-sm" onClick={props.onCancel}>Cancel</button>
        </div>
        <label class="vd-texts-to">
          <span>Their number</span>
          <input type="tel" value={typed} placeholder="(555) 555-0123" aria-label="Their phone number" autoFocus
            onInput={(e) => setTyped(e.currentTarget.value)} />
          {to ? <b>{fmtPhone(to)}</b> : null}
          {area ? <span class="muted">{area.names.join(' / ')}</span> : null}
        </label>
        <div class="vd-texts-tools">
          {lead ? (
            <>
              <span>Lead: <b>{lead.name}</b></span>
              <button class="btn btn-sm" onClick={() => setLead(null)}>Unlink</button>
            </>
          ) : !linking ? (
            <button class="btn btn-sm" onClick={() => setLinking(true)}>Link to a lead (optional)</button>
          ) : null}
        </div>
        {linking ? (
          <LinkBusiness onPick={async (id, name) => { setLead({ id, name }); setLinking(false); }} onCancel={() => setLinking(false)} />
        ) : null}
        {view?.existingThreadId ? (
          <div class="vd-sms-hint" role="note">
            You already have a conversation with {fmtPhone(to)}: this text goes into it, not a new one.
            <button class="btn btn-sm" type="button" onClick={() => navigate(`/texts/${view.existingThreadId}`)}>Open it</button>
          </div>
        ) : null}
        {view?.business && !lead ? (
          <span class="muted" style="font-size:13px">Filed under {view.business.name}: that lead's texting rules apply.</span>
        ) : null}
        {view?.from && !view.blocked ? <span class="muted" style="font-size:13px">From {fmtPhone(view.from.e164)}: {view.from.why}.</span> : null}
      </div>
      {/* AI draft from the conversation it goes into, else from the lead it is filed under; none with neither. */}
      <SmsComposer blocked={blocked} country={view?.country ?? area?.country ?? null} calendlySlug={view?.calendlySlug ?? null}
        bookingLink={!!view?.calendlySlug} onSend={send} focusKey={key} consentNeeded={consentNeeded}
        notice={usPending ? SMS_US_PENDING_NOTE : null}
        assist={view ? smsAssistFor({ threadId: view.existingThreadId, businessId: view.business?.id ?? null, to, canSummarize: false }) : null} />
    </div>
  );
}

export function Texts(props: { params: Record<string, string> }) {
  const me = useMe();
  const admin = me.role === 'admin';
  const composing = props.params.rest === 'new';
  const selected = /^\d{1,12}$/.test(props.params.rest ?? '') ? Number(props.params.rest) : null;
  const [scope, setScope] = useState<'mine' | 'all'>('mine');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [list, setList] = useState<SmsThreadItem[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [users, setUsers] = useState<UserAdmin[] | null>(null);
  const unread = useSmsUnread();
  const seq = useRef(0);

  const loadList = () => {
    const mine = ++seq.current;
    smsApi.threads(scope, unreadOnly).then(
      (l) => { if (mine === seq.current) { setList(l); setListError(null); } },
      (e) => { if (mine === seq.current) setListError(errorMessage(e)); },
    );
  };
  useEffect(() => {
    setList(null);
    loadList();
    const t = setInterval(loadList, LIST_POLL_MS);
    return () => clearInterval(t);
  }, [scope, unreadOnly]);
  // The badge moved (a new text, or one was read elsewhere): refresh the list now rather than at the next poll.
  const lastCount = useRef<number | null>(null);
  useEffect(() => {
    const n = unread?.count ?? null;
    if (lastCount.current !== null && n !== lastCount.current) loadList();
    lastCount.current = n;
  }, [unread?.count]);
  useEffect(() => {
    if (admin) api.get<UserAdmin[]>('/api/users').then((u) => setUsers(Array.isArray(u) ? u : []), () => setUsers([]));
  }, [admin]);

  return (
    <div class="vd-texts">
      <section class="vd-texts-list" aria-label="Conversations">
        <div class="vd-texts-list-head">
          <div class="row" style="justify-content:space-between">
            <h2 style="margin:0">Texts</h2>
            <div class="row" style="gap:6px">
              {unread?.count ? <span class="chip chip-ok">{unread.count} unread</span> : null}
              <button class="btn btn-primary btn-sm" aria-pressed={composing ? 'true' : undefined}
                title="Text a number someone gave you" onClick={() => navigate('/texts/new')}>New text</button>
            </div>
          </div>
          <Segmented value={scope} onChange={setScope}
            options={[{ value: 'mine', label: 'Mine' }, { value: 'all', label: admin ? 'Everyone' : 'All I can see' }]} />
          <label class="row" style="gap:6px"><input type="checkbox" checked={unreadOnly} onChange={(e) => setUnreadOnly(e.currentTarget.checked)} /> Unread only</label>
        </div>
        <div class="vd-texts-items">
          {listError ? <ErrorBanner error={listError} /> : null}
          {!list && !listError ? <div style="padding:12px"><Spinner label="Loading conversations" /></div> : null}
          {list && !list.length ? (
            <p class="muted" style="padding:12px;margin:0">
              {unreadOnly ? 'Nothing unread.' : scope === 'mine'
                ? 'No texts yet. Text a lead from the call screen (Text, or T), or press New text, and replies land here.'
                : 'No conversations you can see yet.'}
            </p>
          ) : null}
          {list?.map((t) => <ThreadRow t={t} on={t.id === selected} showFor={scope === 'all'} />)}
        </div>
      </section>
      {composing ? (
        <NewText onSent={(id) => { loadList(); navigate(`/texts/${id}`); }} onCancel={() => navigate('/texts')} />
      ) : selected ? (
        <Conversation id={selected} users={users} onChanged={loadList} />
      ) : (
        <div class="vd-texts-convo"><Empty>Pick a conversation on the left, or press New text.</Empty></div>
      )}
    </div>
  );
}
